import { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * The white container of the cockpit: `surface`, `radius-sm`, `shadow-raised`
 * and 40px of padding, with an optional title on a 2px `divider` rule, meta
 * text at the right ("Stand: 09.01.2025, 11:21 Uhr") and an action slot.
 *
 * `accent` is not in the design system's contract. Several cockpit artboards
 * draw a panel that carries a state ("Unklar", "Problemfälle") with a 2px
 * inset ring in that state's subtle tint on top of the lift; without a prop
 * every such screen would hand-write the same box-shadow. It is a ring rather
 * than a border so the panel's geometry does not change with it.
 */
const panelStyles = cva('box-border rounded-sm bg-surface p-10 shadow-raised', {
  variants: {
    accent: {
      none: '',
      danger: 'ring-2 ring-inset ring-danger-subtle',
      warning: 'ring-2 ring-inset ring-warning-subtle',
      success: 'ring-2 ring-inset ring-success-subtle',
    },
  },
  defaultVariants: { accent: 'none' },
})

export interface FxPanelProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    VariantProps<typeof panelStyles> {
  /** Shadows the DOM `title` attribute, which a panel never needs. */
  title?: React.ReactNode
  meta?: React.ReactNode
  action?: React.ReactNode
}

export const FxPanel = forwardRef<HTMLElement, FxPanelProps>(
  ({ title, meta, action, accent, className, children, ...props }, ref) => (
    <section ref={ref} className={cn(panelStyles({ accent }), className)} {...props}>
      {title != null && (
        <header className="mb-10 flex flex-wrap items-baseline gap-4 border-b-2 border-divider pb-3">
          <h2 className="m-0 flex-1 text-panel-title">{title}</h2>
          {meta != null && <span className="text-body">{meta}</span>}
          {action}
        </header>
      )}
      {children}
    </section>
  ),
)
FxPanel.displayName = 'FxPanel'
